import React, { useMemo, useState } from 'react';
import { Badge, Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import { ArrowLineRight, CaretDown, CaretRight } from 'phosphor-react';

import { GlassCard } from '../../../../components/glass';
import { IContabilBalancete } from '../../auditoriaInteligente/types';
import {
  NoBalancete,
  achatar,
  chaveDe,
  descreverReciprocas,
  fmtMoney,
  fmtSaldo,
  marcaDoSaldo,
  montarArvore,
  temReciproca,
} from './balancete';

interface Props {
  contas: IContabilBalancete[];
  carregando: boolean;
  /** Busca ativa — força a árvore aberta pra o match não ficar escondido. */
  buscaAtiva: boolean;
  /** Clique numa conta ANALÍTICA abre o Papel de trabalho dela. */
  onAbrirConta: (conta: IContabilBalancete) => void;
}

const COR = {
  ink: '#0F172A',
  muted: '#64748B',
  faint: '#94A3B8',
  linha: 'rgba(15, 23, 42, 0.05)',
  hover: 'rgba(27, 136, 245, 0.05)',
  brand: '#1B88F5',
  warn: '#D97706',
  warnSoft: '#FCF1DD',
  analitica: '#059669',
  analiticaSoft: 'rgba(16, 185, 129, 0.10)',
  sintetica: '#6366F1',
  sinteticaSoft: 'rgba(99, 102, 241, 0.10)',
  // Reciproca ganha cor PROPRIA (roxo) para nao se confundir com o
  // ambar do saldo invertido: sao ressalvas de natureza diferente —
  // uma e' divergencia de dado, a outra e' exigencia da norma.
  reciproca: '#7C3AED',
  reciprocaSoft: 'rgba(124, 58, 237, 0.10)',
} as const;

/** Cabeçalho das colunas — D/C visíveis em TODOS os níveis, não só o saldo. */
function Cabecalho() {
  return (
    <Flex
      align="center"
      gap={2}
      px={3}
      py={2}
      borderBottomWidth="1px"
      borderColor={COR.linha}
      bg="rgba(15, 23, 42, 0.025)"
      position="sticky"
      top={0}
      zIndex={1}
      fontSize="10px"
      fontWeight="700"
      letterSpacing="0.06em"
      textTransform="uppercase"
      color={COR.faint}
    >
      <Box w="16px" flexShrink={0} />
      <Text minW="92px" flexShrink={0}>
        Classificação
      </Text>
      <Text flex={1} minW={0}>
        Conta
      </Text>
      {/* Saldo Anterior é a 1ª coluna de valor do balancete impresso do
          Domínio — o contador confere na mesma ordem do papel dele. */}
      <Text w="140px" textAlign="right" flexShrink={0}>
        Saldo anterior
      </Text>
      <Text w="130px" textAlign="right" flexShrink={0}>
        Débito
      </Text>
      <Text w="130px" textAlign="right" flexShrink={0}>
        Crédito
      </Text>
      <Text w="150px" textAlign="right" flexShrink={0}>
        Saldo
      </Text>
      <Box w="28px" flexShrink={0} />
    </Flex>
  );
}

interface LinhaProps {
  no: NoBalancete;
  aberto: boolean;
  onToggle: (chave: string) => void;
  onAbrirConta: (conta: IContabilBalancete) => void;
}

function Linha({ no, aberto, onToggle, onAbrirConta }: LinhaProps) {
  const chave = chaveDe(no);
  const temFilhos = no.children.length > 0;
  // Só analítica (folha de movimento) abre a composição do saldo.
  const analitica = no.tipo === 'A';
  // `null` no caso normal — a marca só aparece quando há o que ressalvar.
  const marca = marcaDoSaldo(no);

  // A LINHA INTEIRA da analítica é o alvo de clique (o ícone sozinho seria um
  // alvo pequeno demais): é assim que o contador entra no papel de trabalho.
  const abrir = () => onAbrirConta(no);

  let rotuloExpandir: string | undefined;
  if (temFilhos) rotuloExpandir = aberto ? 'Recolher conta' : 'Expandir conta';

  return (
    <Flex
      align="center"
      gap={2}
      px={3}
      py={2}
      pl={`${12 + no.depth * 18}px`}
      borderBottomWidth="1px"
      borderColor={COR.linha}
      _hover={{ bg: COR.hover }}
      // `className="group"` (e não role="group") habilita o _groupHover do
      // Chakra — assim a linha analítica pode ter role="button" de verdade
      // sem perder a revelação do ícone no hover.
      className="group"
      role={analitica ? 'button' : undefined}
      tabIndex={analitica ? 0 : undefined}
      cursor={analitica ? 'pointer' : undefined}
      aria-label={
        analitica
          ? `Abrir papel de trabalho da conta ${no.classificacao} ${no.nome_conta}`
          : undefined
      }
      onClick={analitica ? abrir : undefined}
      onKeyDown={
        analitica
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                abrir();
              }
            }
          : undefined
      }
    >
      <Box
        as={temFilhos ? 'button' : 'div'}
        // `type` não existe na tipagem polimórfica do Box (as='button'|'div'),
        // mas sem ele o button assume type="submit" — o spread mantém o
        // atributo no DOM sem quebrar o TS.
        {...(temFilhos ? { type: 'button' as const } : {})}
        w="16px"
        flexShrink={0}
        color={COR.faint}
        onClick={
          temFilhos
            ? (e: React.MouseEvent) => {
                // Não deixa o expandir/recolher disparar a navegação da linha.
                e.stopPropagation();
                onToggle(chave);
              }
            : undefined
        }
        aria-label={rotuloExpandir}
      >
        {temFilhos &&
          (aberto ? <CaretDown size={13} weight="bold" /> : <CaretRight size={13} weight="bold" />)}
      </Box>

      <Text fontFamily="mono" fontSize="10px" color={COR.faint} minW="92px" flexShrink={0}>
        {no.classificacao || '—'}
      </Text>

      <Flex flex={1} minW={0} align="center" gap={2}>
        <Text
          fontSize="13px"
          color={analitica ? '#475569' : COR.ink}
          fontWeight={analitica ? '500' : '700'}
          isTruncated
          title={no.nome_conta ?? ''}
        >
          {no.nome_conta || '—'}
        </Text>
        {/* Evidencia da ITG 2000 (R1), item 24: esta linha tem conta
            reciproca e a soma da tela NAO a elimina. O title lista QUAL
            conta, de QUAL empresa e com que saldo — sem isso o contador
            sabe que existe mas nao sabe onde conferir. */}
        {temReciproca(no) && (
          <Badge
            flexShrink={0}
            borderRadius="full"
            px={2}
            fontSize="9px"
            fontWeight="700"
            textTransform="none"
            bg={COR.reciprocaSoft}
            color={COR.reciproca}
            title={descreverReciprocas(no)}
          >
            recíproca {(no.reciprocas ?? []).length}
          </Badge>
        )}
        {no.alerta && (
          <Badge
            flexShrink={0}
            borderRadius="full"
            px={2}
            fontSize="9px"
            fontWeight="700"
            textTransform="none"
            bg={COR.warnSoft}
            color={COR.warn}
            title="Saldo invertido para a natureza da conta"
          >
            {no.alerta}
          </Badge>
        )}
      </Flex>

      {/* Saldo anterior: "—" quando a empresa não tem carga de saldo inicial.
          Zero afirmaria conta zerada — o traço diz "não sei", que é a verdade.
          Quando falta, o Saldo ao lado carrega o ponto de ressalva. */}
      <Text
        w="140px"
        textAlign="right"
        flexShrink={0}
        fontSize="12px"
        color={COR.muted}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {no.saldo_anterior === null || no.saldo_anterior === undefined
          ? '—'
          : fmtSaldo(no.saldo_anterior)}
      </Text>
      <Text
        w="130px"
        textAlign="right"
        flexShrink={0}
        fontSize="12px"
        color={COR.muted}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {fmtMoney(no.total_debito)}
      </Text>
      <Text
        w="130px"
        textAlign="right"
        flexShrink={0}
        fontSize="12px"
        color={COR.muted}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {fmtMoney(no.total_credito)}
      </Text>
      <Text
        w="150px"
        textAlign="right"
        flexShrink={0}
        fontSize="12.5px"
        fontWeight="700"
        color={no.alerta ? COR.warn : COR.ink}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {fmtSaldo(no.saldo)}
        {/* Ponto sobrescrito quando o saldo merece ressalva: abertura ausente
            ou divergência entre o nosso cálculo e o do ERP. A explicação vai no
            `title` — um ponto sem tooltip seria enfeite indecifrável. */}
        {marca && (
          <Box
            as="sup"
            title={marca.explicacao}
            color={COR.warn}
            fontSize="13px"
            fontWeight="800"
            ml="2px"
            cursor="help"
            aria-label={marca.explicacao}
          >
            {marca.marca}
          </Box>
        )}
      </Text>

      <Box w="28px" flexShrink={0} textAlign="right">
        {analitica ? (
          // Apenas a AFORDÂNCIA visual — quem trata o clique é a linha inteira
          // (um botão aqui dentro duplicaria o disparo da navegação).
          <Box
            as="span"
            display="inline-flex"
            aria-hidden="true"
            color={COR.brand}
            opacity={0.45}
            _groupHover={{ opacity: 1 }}
          >
            <ArrowLineRight size={14} weight="bold" />
          </Box>
        ) : (
          <Badge
            borderRadius="full"
            fontSize="9px"
            fontWeight="700"
            px={1.5}
            bg={COR.sinteticaSoft}
            color={COR.sintetica}
            title="Conta sintética (agrupadora)"
          >
            S
          </Badge>
        )}
      </Box>
    </Flex>
  );
}

/**
 * Árvore do balancete: sintético → analítico, na ORDEM CONTÁBIL (por
 * `classificacao`, como o backend devolve). Nunca reordenar por nome.
 */
export function BalanceteArvore({ contas, carregando, buscaAtiva, onAbrirConta }: Props) {
  // COMEÇA RECOLHIDO: a tela abre mostrando só o 1º nível do plano (ATIVO,
  // PASSIVO, RECEITAS…). O balancete da 856 tem 226 linhas em 5 níveis —
  // despejar tudo de uma vez é justamente a poluição que a árvore existe para
  // evitar. "Expandir tudo" está ali para quem quiser o corrido.
  //
  // `useState(() => ...)` calcula UMA vez, no primeiro render. Recalcular a
  // cada render reabriria os nós que o usuário fechou.
  const [recolhidos, setRecolhidos] = useState<Set<string>>(
    () => new Set(contas.filter(c => c.tipo === 'S').map(chaveDe)),
  );

  // A lista chega vazia enquanto carrega, então o estado inicial nasceria
  // vazio (= tudo aberto). Quando as contas chegam, recolhe uma vez — e só
  // enquanto o usuário ainda não mexeu, para não desfazer o que ele abriu.
  const [tocou, setTocou] = useState(false);
  React.useEffect(() => {
    if (tocou || !contas.length) return;
    setRecolhidos(new Set(contas.filter(c => c.tipo === 'S').map(chaveDe)));
  }, [contas, tocou]);

  const arvore = useMemo(() => montarArvore(contas), [contas]);
  const visiveis = useMemo(
    () => achatar(arvore, recolhidos, buscaAtiva),
    [arvore, recolhidos, buscaAtiva],
  );

  const toggle = (chave: string) => {
    setTocou(true);
    return setRecolhidos(prev => {
      const next = new Set(prev);
      if (next.has(chave)) next.delete(chave);
      else next.add(chave);
      return next;
    });
  };

  // Recolher tudo = marcar TODA sintética como fechada. Recolher um nó esconde
  // os filhos dele, não ele mesmo, então os grandes grupos (1 Ativo, 2 Passivo…)
  // continuam visíveis — sobra exatamente o 1º nível do plano de contas.
  const recolherTudo = () => {
    setTocou(true);
    return setRecolhidos(new Set(contas.filter(c => c.tipo === 'S').map(chaveDe)));
  };

  /** Abre tudo — e marca que o usuário decidiu, para o efeito não recolher de novo. */
  const expandirTudo = () => {
    setTocou(true);
    setRecolhidos(new Set());
  };

  return (
    <>
      <Flex align="center" justify="space-between" gap={2} mb={3} px={1} wrap="wrap">
        <Flex align="center" gap={2}>
          <Text fontSize="sm" fontWeight="600" color={COR.ink}>
            {visiveis.length} conta(s) visível(eis)
          </Text>
          {carregando && <Spinner size="xs" color={COR.brand} thickness="2px" />}
        </Flex>
        <Flex align="center" gap={2}>
          <Badge
            borderRadius="full"
            fontSize="9px"
            fontWeight="700"
            px={2}
            bg={COR.analiticaSoft}
            color={COR.analitica}
            title="Contas analíticas (folhas de movimento) abrem o papel de trabalho; S marca as sintéticas (agrupadoras)"
          >
            Analítica → abre o papel de trabalho
          </Badge>
          <Button
            size="xs"
            variant="ghost"
            borderRadius="full"
            color={COR.muted}
            onClick={expandirTudo}
          >
            Expandir tudo
          </Button>
          <Button
            size="xs"
            variant="ghost"
            borderRadius="full"
            color={COR.muted}
            onClick={recolherTudo}
          >
            Recolher tudo
          </Button>
        </Flex>
      </Flex>

      <GlassCard intensity="medium" p={0} overflow="hidden">
        <Box overflowX="auto">
          <Box minW="860px">
            <Cabecalho />
            {visiveis.length === 0 ? (
              <Box py={12}>
                <Text fontSize="sm" color={COR.faint} textAlign="center">
                  {carregando
                    ? 'Carregando composição de saldo…'
                    : 'Nenhuma conta para o filtro atual.'}
                </Text>
              </Box>
            ) : (
              visiveis.map(no => (
                <Linha
                  key={chaveDe(no)}
                  no={no}
                  aberto={buscaAtiva || !recolhidos.has(chaveDe(no))}
                  onToggle={toggle}
                  onAbrirConta={onAbrirConta}
                />
              ))
            )}
          </Box>
        </Box>
      </GlassCard>
    </>
  );
}
